// HeatmapGrid.js — the grid itself: axis titles, axis numbers, and cells that
// show their count. Hovering a cell highlights its row and column numbers.
// Hover/click handlers are passed in from the parent.

import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { fadeInCell } from "../../styles/animations";
import { BRAND } from "../../theme";
import {
  AXIS_LABELS,
  EMPTY_CELL,
  HOME_AWAY,
  WINNER_LOSER,
  CELL_TEXT,
  bandFor,
  formatCellCount,
  rgb,
} from "./utils";

// Cap total entry duration so larger seasons don't drag. 600ms feels
// snappy but still readable as a "paint-in" motion.
const ENTRY_MS = 600;
const MAX_STAGGER_STEPS = 18;
export const CELL_GAP = 3;

const TITLE_SX = {
  color: "#e6e8ee",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.16em",
  whiteSpace: "nowrap",
  lineHeight: 1,
};

function AxisTitle({ label, isMobile }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: isMobile ? 14 : 22, height: 2, borderRadius: 1, background: BRAND.gradient }} />
      <Typography component="span" sx={{ ...TITLE_SX, fontSize: isMobile ? 10 : 12 }}>
        {label}
      </Typography>
      <Typography component="span" sx={{ color: BRAND.cyan, fontWeight: 800, fontSize: isMobile ? 12 : 15, lineHeight: 1 }}>
        →
      </Typography>
    </Box>
  );
}

function AxisNumber({ value, active, isMobile }) {
  return (
    <Box
      sx={{
        minWidth: isMobile ? 20 : 28,
        height: isMobile ? 20 : 28,
        px: 0.5,
        borderRadius: isMobile ? "6px" : "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 800,
        fontSize: isMobile ? 11 : 14,
        fontFeatureSettings: '"tnum"',
        color: active ? "#fff" : "#d7dae3",
        background: active ? BRAND.gradient : "rgba(255,255,255,0.05)",
        border: `1px solid ${active ? "transparent" : "rgba(255,255,255,0.08)"}`,
        boxShadow: active ? "0 0 12px rgba(111,93,245,0.55)" : "none",
        transition: "all 140ms ease-out",
      }}
    >
      {value}
    </Box>
  );
}

export default function HeatmapGrid({
  xAxis,
  yAxis,
  cellSize,
  isMobile,
  isAllSeasons,
  axes,
  scoreMap,
  scoreSummaryMap,
  bands,
  highlightKey,
  onCellClick,
  onCellHover,
  onCellLeave,
}) {
  const labels = AXIS_LABELS[axes];
  const [hover, setHover] = useState(null); // { x, y } of the hovered cell
  const yLabelW = isMobile ? 24 : 40;
  const fontSize = Math.max(9, Math.min(18, Math.round(cellSize * 0.27)));
  const radius = cellSize >= 44 ? 6 : 4;

  const glow = {
    zIndex: 2,
    boxShadow: "0 0 0 2px rgba(255,255,255,0.85), 0 8px 22px rgba(111,93,245,0.5)",
    filter: "brightness(1.12)",
  };

  return (
    <Box sx={{ display: "flex", alignItems: "stretch", flexShrink: 0 }}>
      {/* y-axis title, rotated to read bottom-up */}
      <Box
        sx={{
          width: isMobile ? 18 : 30,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pb: isMobile ? 4 : 6,
        }}
      >
        <Box sx={{ transform: "rotate(-90deg)" }}>
          <AxisTitle label={labels.y} isMobile={isMobile} />
        </Box>
      </Box>

      <Box>
        <Box
          role="grid"
          aria-label={`Premier League scorelines: ${labels.x} across, ${labels.y} up`}
          sx={{
            display: "grid",
            gridTemplateColumns: `${yLabelW}px repeat(${xAxis.length}, ${cellSize}px)`,
            gap: `${CELL_GAP}px`,
          }}
        >
          {yAxis.map((y) => (
            <React.Fragment key={y}>
              <Box data-axis-y={y} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", pr: 0.5 }}>
                <AxisNumber value={y} active={hover?.y === y} isMobile={isMobile} />
              </Box>
              {xAxis.map((x) => {
                // Winner/Loser mirrors each winner-first scoreline onto both
                // sides of the diagonal: (1,3) and (3,1) are both "3-1".
                const key =
                  axes === WINNER_LOSER
                    ? `${Math.max(x, y)}-${Math.min(x, y)}`
                    : `${x}-${y}`;
                const count = isAllSeasons
                  ? scoreSummaryMap[key]?.count || 0
                  : scoreMap[key]?.length || 0;
                const band = count ? bandFor(count, bands) : null;
                const label =
                  axes === HOME_AWAY
                    ? `Home ${x}, away ${y}`
                    : `${Math.max(x, y)}-${Math.min(x, y)}`;

                // Diagonal stagger: cells on the same anti-diagonal fade in
                // together, giving a wipe-from-top-left look.
                const step = Math.min(xAxis.indexOf(x) + yAxis.indexOf(y), MAX_STAGGER_STEPS);
                const delayMs = (step / MAX_STAGGER_STEPS) * ENTRY_MS;

                return (
                  <Box
                    key={`${x}-${y}`}
                    data-cell={`${x}-${y}`}
                    role={count ? "button" : undefined}
                    tabIndex={count ? 0 : -1}
                    aria-label={count ? `${label}: ${count.toLocaleString()} match${count === 1 ? "" : "es"}` : undefined}
                    onClick={() => count && onCellClick(key)}
                    onKeyDown={(e) => {
                      if (count && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        onCellClick(key);
                      }
                    }}
                    onMouseEnter={!isMobile ? () => setHover({ x, y }) : undefined}
                    onMouseMove={!isMobile && count ? (e) => onCellHover(e, key, count) : undefined}
                    onMouseLeave={
                      !isMobile
                        ? () => {
                            setHover(null);
                            onCellLeave();
                          }
                        : undefined
                    }
                    sx={{
                      width: cellSize,
                      height: cellSize,
                      borderRadius: `${radius}px`,
                      background: band ? rgb(band.color) : EMPTY_CELL,
                      border: band ? "none" : "1px solid rgba(255,255,255,0.05)",
                      boxShadow: band ? "inset 0 -2px 0 rgba(0,0,0,0.18)" : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: band ? CELL_TEXT : "transparent",
                      fontSize,
                      fontWeight: 800,
                      fontFeatureSettings: '"tnum"',
                      letterSpacing: "-0.02em",
                      cursor: count ? "pointer" : "default",
                      position: "relative",
                      userSelect: "none",
                      opacity: 0,
                      animation: `${fadeInCell} 360ms ease-out forwards`,
                      animationDelay: `${delayMs}ms`,
                      transition:
                        "transform 140ms ease-out, box-shadow 140ms ease-out, filter 140ms ease-out",
                      outline: "none",
                      // Light up the mirrored twin of the hovered cell too.
                      ...(count && key === highlightKey && glow),
                      ...(count && {
                        "&:hover, &:focus-visible": { ...glow, transform: "scale(1.06)" },
                        "&:active": { transform: "scale(0.96)" },
                      }),
                    }}
                  >
                    {count ? formatCellCount(count, cellSize) : null}
                  </Box>
                );
              })}
            </React.Fragment>
          ))}

          {/* x-axis numbers */}
          <Box />
          {xAxis.map((x) => (
            <Box key={x} data-axis-x={x} sx={{ display: "flex", justifyContent: "center", pt: 0.5 }}>
              <AxisNumber value={x} active={hover?.x === x} isMobile={isMobile} />
            </Box>
          ))}
        </Box>

        {/* x-axis title, centred under the cell columns */}
        <Box sx={{ display: "flex", justifyContent: "center", pl: `${yLabelW + CELL_GAP}px`, mt: isMobile ? 1.25 : 1.75 }}>
          <AxisTitle label={labels.x} isMobile={isMobile} />
        </Box>
      </Box>
    </Box>
  );
}
